<template>
  <dd-modal
      :destroy-on-close="true"
      :visible="visible"
      @cancel="close"
      :show-cancel="true"
      width="700px"
  >
    <dd-header-card header="Handmatige controle vereist">
      <div class="flex-col gap-y-md">
        <div v-if="automations.length > 1" class="flex-col gap-y-sm">
          <div class="flex-row justify-content-between">
            <span><strong>{{ handledCount }}</strong> gecontroleerd</span>
            <span><strong>{{ remainingCount }}</strong> te controleren</span>
          </div>
          <a-progress :strokeWidth="10" :percent="percentage"/>
        </div>
        <div class="automation-wrapper">
          <transition name="slide">
            <div class="flex-col gap-md justify-content-between height-stretch"
                 v-if="currentAutomationInterface"
                 :key="currentAutomationInterface.id"
            >
              <AutomationConfirmationCard
                  @done="nextAutomation"
                  :automation="currentAutomationInterface"
                  :is-editing="true"
              ></AutomationConfirmationCard>
              <div class="flex-row justify-content-between align-items-center">
                <a-tooltip placement="top">
                  <template v-slot:title>Niet nu, later controleren</template>
                  <dd-button @click="skip" :outline="true">
                    later controleren
                  </dd-button>
                </a-tooltip>
                <div class="flex-row gap-sm">
                  <a-tooltip placement="top">
                    <template v-slot:title>Nooit uitvoeren</template>
                    <dd-button @click="ignore" :outline="true" type="danger">
                      afkeuren
                    </dd-button>
                  </a-tooltip>
                  <dd-button @click="accept">
                    uitvoeren
                  </dd-button>
                </div>
              </div>
            </div>
            <div class="flex-col height-stretch" v-else>
              <div class="flex-col justify-content-center gap-md m-auto">
                <CheckIcon class="icon-size-xl fill-success"></CheckIcon>
                <span class="text-center">Alles is gecontroleerd!</span>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </dd-header-card>
  </dd-modal>
</template>

<script>
import automationResultApi from '~/api/automation_result'
import AutomationConfirmationCard from './AutomationConfirmationCard.vue'
import automation_action from '~/api/automation_action'
import {mapActions, mapGetters, mapMutations} from 'vuex'
import automationApi from "~/api/automation";

export default {
  name: 'AutomationResultConfirmModal',
  components: { AutomationConfirmationCard },
  props: {
    visible: {
      type: Boolean,
    },
    automations: {
      type: Array,
      required: true,
    }
  },
  data () {
    return {
      automationsPendingConfirmation: [],
      remainingAutomationsPendingConfirmation: [],
      skippedAutomationsIds: [],
      currentIndex: 0
    }
  },
  created () {
    this.getMe()
    this.automationsPendingConfirmation = this.automations.filter(automation => automation.pending_confirmation) || []
    this.remainingAutomationsPendingConfirmation = this.automationsPendingConfirmation
  },
  computed: {
    ...mapGetters(['user']),
    ...mapGetters('$_automation_show', {
      configuredAutomationInterface: 'configuredAutomationInterface',
    }),
    automation_action () {
      return automation_action
    },
    currentAutomationInterface () {
      return this.automationsPendingConfirmation[this.currentIndex]
    },
    currentIsGlobal () {
      return this.currentAutomationInterface?.is_global
    },
    percentage () {
      return Math.round((this.handledCount / this.allCount) * 100)
    },
    remainingCount () {
      return this.remainingAutomationsPendingConfirmation.length
    },
    allCount () {
      return this.automationsPendingConfirmation.length
    },
    handledCount () {
      return this.automationsPendingConfirmation.length - this.remainingAutomationsPendingConfirmation.length
    }
  },
  methods: {
    ...mapActions(['getMe']),
    ...mapMutations('$_automation', {
      setPendingConfirmationCount: 'SET_PENDING_CONFIRMATION_COUNT',
    }),
    ...mapMutations('$_automation_show', {
      setConfiguredAutomationInterfacePendingConfirmationCount: 'SET_CONFIGURED_AUTOMATION_INTERFACE_PENDING_CONFIRMATION_COUNT',
    }),
    close () {
      this.$emit('close', this.remainingAutomationsPendingConfirmation.map(automation => automation.id))
    },
    skip () {
      if (this.currentIsGlobal) {
        automationResultApi.skipForGlobalAutomationConfiguration(this.currentAutomationInterface.id)
          .then(() => {
            this.nextAutomation()
          });
      } else {
        automationResultApi.skipForAutomation(this.currentAutomationInterface.id)
          .then(() => {
            this.nextAutomation()
          });
      }
    },
    ignore () {
      if (this.currentIsGlobal) {
        automationResultApi
          .ignoreUnprocessedByGlobalAutomationConfiguration(this.currentAutomationInterface?.id, this.user?.id)
          .then(() => {
            this.nextAutomation()
          })
      } else {
        automationResultApi.ignoreUnprocessedByAutomation(this.currentAutomationInterface?.id, this.user?.id)
          .then(() => {
            this.nextAutomation()
          })
      }
    },
    accept () {
      if (this.currentIsGlobal) {
        automationResultApi
          .processUnprocessedByGlobalAutomationConfiguration(this.currentAutomationInterface?.id, this.user?.id)
          .then(() => {
            this.nextAutomation()
          })
      } else {
        automationResultApi.processUnprocessedByAutomation(this.currentAutomationInterface?.id, this.user?.id)
          .then(() => {
            this.nextAutomation()
          })
      }
    },
    nextAutomation () {
      this.remainingAutomationsPendingConfirmation = this.remainingAutomationsPendingConfirmation
        .filter(automation => automation.id !== this.currentAutomationInterface.id)

      this.setPendingConfirmationCount(this.remainingAutomationsPendingConfirmation.length)

      // update $_automation_show pending_confirmation count
      if (this.configuredAutomationInterface !== null) {
        this.setConfiguredAutomationInterfacePendingConfirmationCount(0)
      }

      if (this.remainingAutomationsPendingConfirmation.length === 0) {
        setTimeout(() => {
          this.$emit('close')
        }, 1000)
      }

      this.currentIndex++
    }
  }
}
</script>

<style lang="scss">
@import "~@/_default.scss";

.slide-enter-active,
.slide-leave-active {
  position: absolute;
  transition: all .3s ease-in-out;
}

.slide-leave-active {
  right: $spacing-md;
}

.slide-enter {
  position: absolute;
  transform: translateX(100%);
}

.slide-enter-to {
  position: absolute;
  transform: translateX(0);
}

.slide-leave-to {
  position: absolute;
  transform: translateX(-100%);
}

.slide-leave {
  position: absolute;
  transform: translateX(0);
}
</style>

<style lang="scss" scoped>
@import "~@/_default.scss";

.automation-wrapper {
  max-width: 100%;
  height: 300px;
  overflow-x: clip;
  position: relative;
}

.content {
  background-color: white;
  padding: $spacing-lg;
  border-radius: $border-radius-base;

  p {
    margin: 0 !important;
    font-size: $font-size-small !important;
  }
}
</style>

